// src/services/IVRService.js
import axios from 'axios'

function unwrapList(res) {
  const d = res?.data ?? res
  if (!d) return []
  if (Array.isArray(d)) return d
  if (Array.isArray(d.data)) return d.data
  if (Array.isArray(d.rows)) return d.rows
  if (Array.isArray(d.items)) return d.items
  if (d.data && typeof d.data === 'object') {
    const vals = Object.values(d.data).find(v => Array.isArray(v))
    if (vals) return vals
  }
  return []
}

function unwrapOne(res) {
  const d = res?.data ?? res
  if (!d) return null
  if (d.data && typeof d.data === 'object') return d.data
  return d
}

function str(v) {
  return String(v ?? '').trim()
}

function genId(prefix = 'id') {
  const rnd = Math.random().toString(36).slice(2, 10)
  const ts = Date.now().toString(36)
  return `${prefix}_${ts}_${rnd}`.slice(0, 255)
}

const IVRService = {
  unwrapList,
  unwrapOne,
  genId,

  async listIvrs(params = {}) {
    return axios.get('ivr_lists', { params })
  },

  async getIvr(id, params = {}) {
    const ivrId = str(id)
    if (!ivrId) throw new Error('Missing IVRID')
    return axios.get(`ivr_lists/${encodeURIComponent(ivrId)}`, { params })
  },

  async createIvr(payload = {}) {
    return axios.post('ivr_lists', payload)
  },

  async updateIvr(id, payload = {}) {
    const ivrId = str(id)
    if (!ivrId) throw new Error('Missing IVRID')
    return axios.put(`ivr_lists/${encodeURIComponent(ivrId)}`, payload)
  },

  async destroyIvr(id) {
    const ivrId = str(id)
    if (!ivrId) throw new Error('Missing IVRID')
    return axios.delete(`ivr_lists/${encodeURIComponent(ivrId)}`)
  },

  async listMenus(params = {}) {
    return axios.get('ivr_menus', { params })
  },

  async listMenuByIvr(ivrId) {
    const id = str(ivrId)
    if (!id) throw new Error('Missing IVRID')

    const res = await IVRService.listMenus()
    const rows = unwrapList(res)

    const filtered = rows.filter(r => str(r?.IVRID ?? r?.ivr_id) === id)
    return { data: { data: filtered } }
  },

  async createMenuItem(payload = {}) {
    const out = { ...payload }
    if (!out.IVRItemID && !out.ivr_item_id) out.IVRItemID = genId('ivritem')
    return axios.post('ivr_menus', out)
  },

  async updateMenuItem(id, payload = {}) {
    const menuId = str(id)
    if (!menuId) throw new Error('Missing IVRItemID')
    return axios.put(`ivr_menus/${encodeURIComponent(menuId)}`, payload)
  },

  async destroyMenuItem(id) {
    const menuId = str(id)
    if (!menuId) throw new Error('Missing IVRItemID')
    return axios.delete(`ivr_menus/${encodeURIComponent(menuId)}`)
  },

  async listRecordings(params = {}) {
    return axios.get('ivr_recordings', { params })
  },

  // ✅ correct start route
  async startRecording(name) {
    const n = str(name)
    if (!n) throw new Error('Missing recording name')
    return axios.post('ivr_recordings/record', { name: n })
  },

  // ✅ new finish route
  async finishRecording({ file_token, name }) {
    const t = str(file_token)
    const n = str(name)
    if (!t) throw new Error('Missing file_token')
    if (!n) throw new Error('Missing recording name')
    return axios.post('ivr_recordings/record/finish', { file_token: t, name: n })
  },

  async updateRecording(id, payload = {}) {
    const rid = str(id)
    if (!rid) throw new Error('Missing RecordingID')
    return axios.put(`ivr_recordings/${encodeURIComponent(rid)}`, payload)
  },

  async destroyRecording(id) {
    const rid = str(id)
    if (!rid) throw new Error('Missing RecordingID')
    return axios.delete(`ivr_recordings/${encodeURIComponent(rid)}`)
  },

  async rolloverOptions(params = {}) {
    return axios.get('endpoints/rollover-options', { params })
  }
}

export default IVRService
